import './activationPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { AuthType } from '@thesavingsgroup/auth/AuthType';
import { PHONE_NUMBER_PATTERN } from '@thesavingsgroup/regex';
import { toDigits } from '@thesavingsgroup/utils/toDigits';
import { toPhoneString } from '@thesavingsgroup/utils/toPhoneString';
import { ReactNode, useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import vehicleSUVSideView from '../../assets/vehicle-suv-sideview-one.png';
import { Alert } from '../../components/Alert/Alert';
import { Button } from '../../components/Button/Button';
import { Checkbox } from '../../components/Checkbox/Checkbox';
import { Header } from '../../components/Header/Header';
import { LanderImage } from '../../components/LanderImage/LanderImage';
import { NumberInput } from '../../components/NumberInput/NumberInput';
import { Rate } from '../../components/Rate/Rate';
import useApiStore from '../../store/apiStore/useApiStore';
import useLeadChannelStore from '../../store/leadChannelStore/useLeadChannelStore';
import useParameterStore from '../../store/parameterStore/useParameterStore';
import { DeliverPinLocation } from '../../store/pinStore/enums/DeliverPinLocation';
import usePinStore from '../../store/pinStore/usePinStore';
import useRouteStore from '../../store/routeStore/useRouteStore';
import useThemeStore from '../../store/themeStore/useThemeStore';
import { locator } from '../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { isLoading } = useApiStore();
  const { t } = useTranslation();
  const {
    primaryPhoneNumber: pinDeliveredPhoneNumber,
    deliverPin,
    deliverPinFailedMessage,
  } = usePinStore();
  const {
    authType,
    collectAgreeToTerms,
    coApplicantCollectAgreeToTerms,
    firstName,
    hasDuplicateApplication,
    monthlyPaymentAmount,
    apr,
    term,
    phoneNumber: parameterPhoneNumber,
    vehicleId,
  } = useParameterStore();
  const {
    computed: { displayLabel },
  } = useLeadChannelStore();
  const { setAuthRedirectPath } = useRouteStore();

  const form = useForm({
    mode: 'onTouched',
    criteriaMode: 'firstError',
    defaultValues: {
      /*
       In the case where the user has already entered a phone number, had a pin delivered,
       and pressed the back button we should display the phone number that was last entered.

       Otherwise, we should display the phone number that was passed in as a query parameter.
      */
      primaryPhoneNumberValue: toPhoneString(
        pinDeliveredPhoneNumber || parameterPhoneNumber || '',
      ),
      termsValue: false,
    },
    delayError: 200,
  });

  const {
    handleSubmit,
    register,
    watch,
    formState: { errors, isValid },
  } = form;

  const error = new URLSearchParams(window.location.search).get('error');

  const displayOfferInformation = [
    firstName,
    monthlyPaymentAmount,
    apr,
    term,
  ].every(Boolean);

  const shouldIncludeApplicantAgreeToTerms = collectAgreeToTerms === 'true';
  const shouldIncludeCoapplicantAgreeToTerms =
    coApplicantCollectAgreeToTerms === 'true';

  const displayAgreeToTerms =
    shouldIncludeApplicantAgreeToTerms || shouldIncludeCoapplicantAgreeToTerms;

  const subtitleText = displayOfferInformation
    ? t('ActivationPage.offerSubtitle')
    : t('ActivationPage.subtitle');

  const phoneNumberValidation = {
    setValueAs: (value: string): string => toDigits(value),
    required: t('validation.phone.required'),
    pattern: {
      value: PHONE_NUMBER_PATTERN,
      message: t('validation.phone.invalid'),
    },
  };

  const termsValidation = {
    required: t('validation.terms.required'),
  };

  const handleRedirect = (): void => {
    usePinStore.setState({ deliverPinFailedMessage: null });
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { primaryPhoneNumberValue, termsValue } = values;

    if (!primaryPhoneNumberValue || (displayAgreeToTerms && !termsValue)) {
      return;
    }

    await deliverPin({
      deliverPinLocation: DeliverPinLocation.SIGN_IN,
      phone: primaryPhoneNumberValue,
      purpose: authType || AuthType.ACCOUNT_ACTIVATION_USING_PIN,
    });

    const { deliverPinSuccess } = usePinStore.getState();

    if (deliverPinSuccess) {
      navigate('/activation/pin-verify');
    }
  };

  useEffect((): void => {
    // The applicant's submission was treated as a duplicate, so send them to the
    // page that explains what happened rather than straight to the application
    if (hasDuplicateApplication === 'true') {
      setAuthRedirectPath('/duplicate-application');

      return;
    }

    // Ensure the user is redirected to the application page upon successfully authentication
    if (vehicleId) {
      setAuthRedirectPath(`/dashboard/application/${vehicleId}`);
    }
  }, [hasDuplicateApplication, vehicleId]);

  return (
    <ThemeProvider theme={theme}>
      <div className="page activation-page">
        <Header showLogo splitLayout />

        <main>
          <LanderImage
            alt={t('imageAlts.vehicleSUVSideView')}
            defaultImage={vehicleSUVSideView}
          />
          <form
            data-testid={locator.pages.activation.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.activation.form.h1}>
                {firstName &&
                  displayLabel &&
                  t('ActivationPage.titleFull', {
                    firstName,
                    displayLabel,
                  })}

                {firstName &&
                  !displayLabel &&
                  t('ActivationPage.titleNameOnly', {
                    firstName,
                  })}

                {!firstName &&
                  displayLabel &&
                  t('ActivationPage.titleLabelOnly', {
                    displayLabel,
                  })}

                {!firstName &&
                  !displayLabel &&
                  t('ActivationPage.titleDefault')}
              </h1>
              <h2 data-testid={locator.pages.activation.form.h2}>
                {subtitleText}
              </h2>

              {displayOfferInformation && (
                <Rate
                  apr={apr}
                  monthlyPaymentAmount={monthlyPaymentAmount}
                  term={term}
                />
              )}

              {error && (
                <div
                  style={{
                    marginBottom: deliverPinFailedMessage ? '1rem' : '2rem',
                  }}
                >
                  <Alert
                    data-testid={locator.pages.activation.alert.parameterError}
                    severity="error"
                  >
                    {t(`errors.parameter.${error}`)}
                  </Alert>
                </div>
              )}

              {deliverPinFailedMessage && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={locator.pages.activation.alert.deliverPinError}
                    severity="error"
                  >
                    <Trans
                      components={{
                        signin: (
                          <Link
                            data-testid={
                              locator.pages.activation.alert.signInLink
                            }
                            to="/sign-in"
                            onClick={handleRedirect}
                          />
                        ),
                        signup: (
                          <Link
                            data-testid={
                              locator.pages.activation.alert.signUpLink
                            }
                            to="/sign-up"
                            onClick={handleRedirect}
                          />
                        ),
                      }}
                    >
                      {deliverPinFailedMessage}
                    </Trans>
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <NumberInput
                  {...register(
                    'primaryPhoneNumberValue',
                    phoneNumberValidation,
                  )}
                  autoComplete="tel-national"
                  data-testid={locator.pages.activation.form.phoneInput}
                  error={errors.primaryPhoneNumberValue}
                  errorTestId={locator.pages.activation.form.phoneInputError}
                  label={t('ActivationPage.phoneNumber')}
                  phone
                />
              </div>

              {displayAgreeToTerms && (
                <div style={{ marginBottom: '3rem' }}>
                  <Checkbox
                    {...register('termsValue', termsValidation)}
                    checked={watch('termsValue', false)}
                    description={
                      <Trans
                        components={{
                          terms: (
                            <a
                              data-testid={
                                locator.pages.activation.form.termsLink
                              }
                              href="https://www.thesavingsgroup.com/terms-of-use/"
                              rel="noreferrer"
                              target="_blank"
                            />
                          ),
                          privacy: (
                            <a
                              data-testid={
                                locator.pages.activation.form.privacyLink
                              }
                              href="https://www.thesavingsgroup.com/privacy-policy/"
                              rel="noreferrer"
                              target="_blank"
                            />
                          ),
                        }}
                        i18nKey={'ActivationPage.acknowledgement'}
                      />
                    }
                    error={errors.termsValue}
                    errorTestId={
                      locator.pages.activation.form.termsCheckboxError
                    }
                    label={
                      shouldIncludeCoapplicantAgreeToTerms
                        ? t('ActivationPage.agreeToFollowingCoapplicant')
                        : t('ActivationPage.agreeToFollowing')
                    }
                    testId={locator.pages.activation.form.termsCheckbox}
                  />
                </div>
              )}

              <Button
                data-testid={locator.pages.activation.form.submitButton}
                disabled={!isValid}
                loader={isLoading}
                loaderPosition="start"
                fullWidth
                submit
              >
                {t('ActivationPage.continue')}
              </Button>
            </div>
            <footer data-testid={locator.pages.activation.footer.root}>
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>
    </ThemeProvider>
  );
}

Component.displayName = 'ActivationPage';
